import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';

// Module-level: persists across component mount/unmount cycles so the modal
// is never shown again for a state that was already acknowledged this session.
let _lastShownPermission = null;

/**
 * Custom hook to get user's current geolocation
 * @param {Object} options - Geolocation options
 * @param {boolean} options.enableHighAccuracy - Use high accuracy mode (default: false)
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
 * @param {number} options.maximumAge - Cache age in milliseconds (default: 0)
 * @param {boolean} options.watchPosition - Watch position continuously (default: false)
 * @param {boolean} options.showPermissionModal - Show modal when permission is denied (default: true)
 * @param {string} options.modalTitle - Custom modal title (default: 'Location Permission Required')
 * @param {string} options.modalMessage - Custom modal message
 * @returns {Object} { lat: number | null, lng: number | null, loading: boolean, error: Error | null, PermissionModal: ReactNode }
 */
function useGetCurrentLocation(options = {}) {
    const { enableHighAccuracy = false, timeout = 10000, maximumAge = 0, watchPosition = false } = options;

    const [location, setLocation] = useState({ lat: null, lng: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [permissionState, setPermissionState] = useState(null);
    const watchIdRef = useRef(null);

    useEffect(() => {
        if (!navigator.geolocation) {
            const unsupportedError = new Error('Geolocation is not supported by this browser');
            setError(unsupportedError);
            setLoading(false);
            console.warn('Geolocation is not supported by this browser.');
            return () => { };
        }

        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions
                .query({ name: 'geolocation' })
                .then((result) => {
                    setPermissionState(result.state);

                    const handleChange = () => setPermissionState(result.state);
                    result.addEventListener('change', handleChange);
                })
                .catch(() => {
                    setPermissionState(null);
                });
        }

        const geolocationOptions = {
            enableHighAccuracy,
            timeout,
            maximumAge,
        };

        const handleSuccess = (position) => {
            const { latitude, longitude } = position.coords;
            setLocation({ lat: latitude, lng: longitude });
            setLoading(false);
            setError(null);
            setPermissionState('granted');
        };

        const handleError = (err) => {
            let errorMessage = 'Unable to retrieve your location';

            switch (err.code) {
                case err.PERMISSION_DENIED:
                    errorMessage = 'Location access denied by user';
                    setPermissionState('denied');

                    break;
                case err.POSITION_UNAVAILABLE:
                    errorMessage = 'Location information unavailable';
                    break;
                case err.TIMEOUT:
                    errorMessage = 'Location request timed out';
                    break;
                default:
                    errorMessage = 'An unknown error occurred';
                    break;
            }

            setError(errorMessage);
            setLoading(false);
            setLocation({ lat: null, lng: null });

            console.warn('Error getting location:', errorMessage, err);
        };

        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }

        if (watchPosition) {
            watchIdRef.current = navigator.geolocation.watchPosition(handleSuccess, handleError, geolocationOptions);
        } else {
            navigator.geolocation.getCurrentPosition(handleSuccess, handleError, geolocationOptions);
        }

        return () => {
            if (watchIdRef.current !== null && watchPosition) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, [enableHighAccuracy, timeout, maximumAge, watchPosition]);

    useEffect(() => {
        if (!permissionState) return;

        // Don't show modal if permission is granted
        if (permissionState === 'granted') {
            _lastShownPermission = 'granted';
            return;
        }

        // Only show modal when the state changes to something we haven't shown yet this session
        if (_lastShownPermission !== permissionState) {
            Modal.destroyAll();

            if (permissionState === 'prompt') {
                Modal.info({
                    title: 'Allow Location Access',
                    content: 'Please allow your browser location to find the best services near you.',
                    okText: 'Got it',
                });
            } else if (permissionState === 'denied') {
                Modal.info({
                    title: 'Location Permission Required',
                    content:
                        "Location access is blocked. Please open your browser's site settings, allow location access, then refresh the page to continue.",
                    okText: 'Got it',
                });
            }

            _lastShownPermission = permissionState;
        }
    }, [permissionState]);

    useEffect(() => {
        return () => {
            Modal.destroyAll();
        };
    }, []);
    return {
        ...location,
        loading,
        error,
        permissionState,
    };
}

export default useGetCurrentLocation;
